import { useEffect, useState } from "react";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  isPushSupported, isStandalone, isIOS, hasActiveSubscription, enablePushAlerts,
} from "@/lib/push";

// Non-PII flag: user dismissed the post-install alerts invitation.
const INVITE_KEY = "ddf_push_invite_dismissed";

function usePushState() {
  const supported = isPushSupported();
  const iosNeedsInstall = !supported && isIOS() && !isStandalone();
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(supported ? Notification.permission : "default");
  useEffect(() => {
    hasActiveSubscription().then(setSubscribed).catch(() => {});
  }, []);
  return { supported, iosNeedsInstall, subscribed, setSubscribed, permission, setPermission };
}

export default function PushAlerts() {
  const st = usePushState();
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    if (!st.supported || Notification.permission !== "default" || localStorage.getItem(INVITE_KEY)) return;
    let timer;
    // Wait until the first-visit subscribe popup has been closed before inviting.
    const invite = () => {
      timer = setTimeout(() => {
        if (localStorage.getItem("ddf_subscribe_dismissed")) setInviteOpen(true);
        else invite();
      }, 3000);
    };
    if (isStandalone()) invite();
    window.addEventListener("appinstalled", invite);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("appinstalled", invite);
    };
  }, [st.supported]);

  const enable = async () => {
    if (st.iosNeedsInstall) {
      toast.info("On iPhone/iPad, add DDF eFlyer to your Home Screen first, then open it and tap the bell.");
      return;
    }
    setBusy(true);
    try {
      const result = await enablePushAlerts();
      st.setPermission(result);
      if (result === "granted") {
        st.setSubscribed(true);
        toast.success("Promo alerts are on! We'll notify you when new promos go live.");
      } else {
        toast.error("Notifications are blocked. You can allow them in your browser settings.");
      }
    } catch {
      toast.error("Could not turn on promo alerts. Please try again.");
    } finally {
      setBusy(false);
      setInviteOpen(false);
    }
  };

  const dismissInvite = () => {
    localStorage.setItem(INVITE_KEY, "1");
    setInviteOpen(false);
  };

  const showBell = (st.supported || st.iosNeedsInstall) && !st.subscribed && st.permission !== "denied";

  return (
    <>
      {showBell && (
        <button
          type="button"
          className="ddf-push-bell"
          onClick={enable}
          disabled={busy}
          data-testid="push-alerts-bell-btn"
        >
          <Bell size={16} /> <span>Get promo alerts</span>
        </button>
      )}
      <Dialog open={inviteOpen} onOpenChange={(v) => !v && dismissInvite()}>
        <DialogContent className="ddf-push-invite max-w-sm" data-testid="push-invite-dialog">
          <div className="ddf-push-invite-icon"><BellRing size={28} /></div>
          <DialogTitle className="ddf-push-invite-title">Never miss a deal</DialogTitle>
          <DialogDescription className="ddf-push-invite-desc">
            Turn on promo alerts and we'll let you know the moment a new Departures or Arrivals promotion goes live.
          </DialogDescription>
          <Button onClick={enable} disabled={busy} className="ddf-push-invite-btn" data-testid="push-invite-enable-btn">
            Turn on alerts
          </Button>
          <button type="button" className="ddf-push-invite-skip" onClick={dismissInvite} data-testid="push-invite-dismiss-btn">
            Not now
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
